import { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import TypingText from './TypingText'
import { CHAT_CONVERSATIONS } from '@/data/demoScripts'
import { MODELS } from '@/components/chat/v2/data'
import { ModelGlyph } from '@/components/chat/v2/atoms'

// State machine for one conversation cycle:
//   'typing-question'   — user message types into the chat
//   'showing-question'  — question fully shown, brief pause before AI
//   'typing-response'   — AI message types in (progressive scripture)
//   'done'              — both messages shown; longer pause before next
// At the end of 'done', advance conversationIndex (rotates through 8
// conversations: 2 from each of Grace → Logos → Spurgeon → Ezra) and
// reset to typing-question.
//
// TypingText reads `speed` as characters per second. Responses are
// 2-3 paragraphs (~700-1100 chars), so we compute response speed per
// turn — always aim for ~5.5s of typing regardless of length, clamped
// to a sane range so short replies don't blur and long ones don't drag.
const QUESTION_HOLD_MS = 500
const FINISHED_HOLD_MS = 4000
const QUESTION_TYPING_SPEED = 60
const RESPONSE_TARGET_SECONDS = 5.5
const RESPONSE_SPEED_MIN = 120
const RESPONSE_SPEED_MAX = 230

function computeResponseSpeed(text) {
  return Math.min(
    RESPONSE_SPEED_MAX,
    Math.max(RESPONSE_SPEED_MIN, text.length / RESPONSE_TARGET_SECONDS)
  )
}

// Paragraph spacing inside the AI bubble — generous so 2-3 paragraph
// replies don't read as one blob. The real chat's MarkdownMessage uses
// `<p className="mb-2">` for paragraphs; ours uses a slightly wider gap
// since each demo paragraph runs longer.
const PARAGRAPH_GAP = '1.2em'

// Mirror of MarkdownMessage's scripture matcher (primary refs only — no
// comma-continuation sticky scan, since each demo ref includes its book).
// `\b` at the start guarantees the leading word never gets pulled in
// (so "and Romans 8:26" highlights "Romans 8:26", never "and Romans...").
const SCRIPTURE_RE =
  /\b(\d?\s?[A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s+(\d{1,3}):(\d{1,3}(?:[–-]\d{1,3})?)/g

// Find every scripture reference in `text`, returning {start, end, value}
// sorted by start. Used by the progressive renderer below.
function findScriptureRefs(text) {
  const refs = []
  SCRIPTURE_RE.lastIndex = 0
  let m
  while ((m = SCRIPTURE_RE.exec(text)) !== null) {
    refs.push({ start: m.index, end: m.index + m[0].length, value: m[0] })
  }
  return refs
}

// Renders streaming text up to `currentLen` with progressive scripture
// highlighting. As soon as a verse reference is fully typed (cursor has
// passed its closing character), it flips to gold + semibold. Refs that
// are still mid-typing render as plain text so the highlight pops in
// only on completion, not partway through.
function renderProgressive(fullText, currentLen, refs) {
  const parts = []
  let pos = 0
  let key = 0
  for (const ref of refs) {
    if (ref.start >= currentLen) break // ref hasn't started typing yet
    if (pos < ref.start) {
      const sliceEnd = Math.min(ref.start, currentLen)
      parts.push(<Fragment key={`t-${key++}`}>{fullText.slice(pos, sliceEnd)}</Fragment>)
    }
    if (ref.end <= currentLen) {
      // Reference fully typed — flip to styled span.
      parts.push(
        <span key={`s-${key++}`} style={{ color: '#e9c349', fontWeight: 600 }}>
          {ref.value}
        </span>
      )
      pos = ref.end
    } else {
      // Reference mid-typing — render the typed portion plain and stop.
      parts.push(<Fragment key={`p-${key++}`}>{fullText.slice(ref.start, currentLen)}</Fragment>)
      return parts
    }
  }
  // Tail after the last fully-typed ref.
  if (pos < currentLen) {
    parts.push(<Fragment key={`t-${key++}`}>{fullText.slice(pos, currentLen)}</Fragment>)
  }
  return parts
}

// Splits a response into paragraphs on `\n\n` and records each
// paragraph's absolute offset range so the per-paragraph renderer can
// clip both the visible text and the scripture refs to its own slice.
function splitParagraphs(text) {
  const out = []
  let offset = 0
  for (const chunk of text.split('\n\n')) {
    out.push({ chunk, start: offset, end: offset + chunk.length })
    offset += chunk.length + 2 // account for the `\n\n` separator
  }
  return out
}

// Types a multi-paragraph string char-by-char, applying progressive
// scripture highlighting as each reference completes. Cursor lives in
// whichever paragraph is currently being typed.
function TypingResponse({ text, speed, onComplete }) {
  const [currentLen, setCurrentLen] = useState(0)
  const onCompleteRef = useRef(onComplete)

  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  useEffect(() => {
    setCurrentLen(0)
    if (!text) {
      onCompleteRef.current?.()
      return
    }
    let i = 0
    const id = setInterval(() => {
      i += 1
      setCurrentLen(i)
      if (i >= text.length) {
        clearInterval(id)
        setTimeout(() => onCompleteRef.current?.(), 250)
      }
    }, 1000 / speed)
    return () => clearInterval(id)
  }, [text, speed])

  const allRefs = findScriptureRefs(text)
  const paragraphs = splitParagraphs(text)
  const cursorVisible = currentLen < text.length

  return (
    <>
      {paragraphs.map((p, i) => {
        const localLen = Math.max(0, Math.min(currentLen - p.start, p.chunk.length))
        if (localLen <= 0) return null
        const localRefs = allRefs
          .filter(r => r.start >= p.start && r.end <= p.end)
          .map(r => ({ start: r.start - p.start, end: r.end - p.start, value: r.value }))
        const isCurrentParagraph = currentLen > p.start && currentLen < p.end
        return (
          <p
            key={i}
            className="leading-relaxed"
            style={{ marginBottom: i < paragraphs.length - 1 ? PARAGRAPH_GAP : 0 }}
          >
            {renderProgressive(p.chunk, localLen, localRefs)}
            {isCurrentParagraph && cursorVisible && (
              <span
                aria-hidden="true"
                className="inline-block w-[1px] ml-0.5 animate-pulse"
                style={{
                  backgroundColor: 'currentColor',
                  height: '1em',
                  verticalAlign: 'text-bottom',
                }}
              />
            )}
          </p>
        )
      })}
    </>
  )
}

// Same progressive renderer applied to the FINAL fully-typed response —
// keeps the post-typing visual identical (refs stay gold, paragraphs
// stay separated). Avoids any swap-from-typing-to-static flicker.
function StaticResponse({ text }) {
  const allRefs = findScriptureRefs(text)
  const paragraphs = splitParagraphs(text)
  return (
    <>
      {paragraphs.map((p, i) => {
        const localRefs = allRefs
          .filter(r => r.start >= p.start && r.end <= p.end)
          .map(r => ({ start: r.start - p.start, end: r.end - p.start, value: r.value }))
        return (
          <p
            key={i}
            className="leading-relaxed"
            style={{ marginBottom: i < paragraphs.length - 1 ? PARAGRAPH_GAP : 0 }}
          >
            {renderProgressive(p.chunk, p.chunk.length, localRefs)}
          </p>
        )
      })}
    </>
  )
}

export default function ChatDemo() {
  const [conversationIndex, setConversationIndex] = useState(0)
  const [phase, setPhase] = useState('typing-question')
  const timeoutRef = useRef(null)

  const conversation = CHAT_CONVERSATIONS[conversationIndex]
  const model = MODELS[conversation.voiceId]

  const advanceToNextConversation = useCallback(() => {
    setConversationIndex(idx => (idx + 1) % CHAT_CONVERSATIONS.length)
    setPhase('typing-question')
  }, [])

  const handleQuestionComplete = useCallback(() => {
    setPhase('showing-question')
    timeoutRef.current = setTimeout(() => {
      setPhase('typing-response')
    }, QUESTION_HOLD_MS)
  }, [])

  const handleResponseComplete = useCallback(() => {
    setPhase('done')
    timeoutRef.current = setTimeout(advanceToNextConversation, FINISHED_HOLD_MS)
  }, [advanceToNextConversation])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  const questionShown = phase !== 'typing-question'
  const aiVisible = phase === 'typing-response' || phase === 'done'
  const responseShown = phase === 'done'

  return (
    <div
      className="relative rounded-2xl overflow-hidden p-5 md:p-7 flex flex-col"
      style={{
        backgroundColor: '#0b1229',
        border: '1px solid rgba(233,195,73,0.15)',
        // Fixed height (not minHeight) so the card doesn't grow as longer
        // responses type in — the bubble fills space within a locked frame.
        height: 620,
      }}
    >
      {/* Soft gold radial wash — echoes the real Chat surface */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(233,195,73,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Conversation thread — scrolls when a long Logos/Ezra response
          types in past the visible height. */}
      <div className="relative flex flex-col gap-4 flex-1 overflow-y-auto dark-scrollbar pr-1">
        {/* User message — right-aligned, gold pill (matches real chat:
            see DesktopTurnBlock / TurnBlock — user bubble uses var(--gold)
            background with var(--text-on-gold) text). */}
        <div className="flex justify-end">
          <div
            className="max-w-[85%]"
            style={{
              padding: '10px 16px',
              borderRadius: 18,
              borderTopRightRadius: 6,
              backgroundColor: 'var(--gold)',
              color: 'var(--text-on-gold)',
              fontFamily: 'var(--font-body)',
              fontSize: 13.5,
              fontWeight: 500,
              lineHeight: 1.5,
              boxShadow: '0 0 14px rgba(233,195,73,0.18)',
            }}
          >
            {questionShown ? (
              conversation.question
            ) : (
              <TypingText
                key={`q-${conversationIndex}`}
                text={conversation.question}
                speed={QUESTION_TYPING_SPEED}
                onComplete={handleQuestionComplete}
                showCursor
              />
            )}
          </div>
        </div>

        {/* AI response — left-aligned. ModelGlyph avatar + voice label
            eyebrow + accent-tinted bubble. Mirrors VoiceCard.jsx so the
            demo looks identical to the real chat. */}
        <AnimatePresence mode="wait">
          {aiVisible && (
            <motion.div
              key={`ai-${conversationIndex}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className="flex items-start gap-2.5"
              style={{ maxWidth: '95%' }}
            >
              <ModelGlyph model={model} size={28} glow={false} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Voice eyebrow — small-caps name in the voice's accent
                    color (matches VoiceCard's showVoiceLabel block). */}
                <div
                  className="px-1 mb-1.5"
                  style={{
                    fontFamily: 'var(--font-label)',
                    fontSize: 9.5,
                    fontWeight: 600,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: model.accent,
                  }}
                >
                  {model.name}
                </div>
                {/* AI bubble — bumped padding + leading to breathe with
                    multi-paragraph content. */}
                <div
                  style={{
                    padding: '13px 16px',
                    borderRadius: 14,
                    borderTopLeftRadius: 4,
                    background: 'rgba(20,26,50,0.7)',
                    border: `1px solid ${model.accent}33`,
                    color: '#dce1ff',
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  {responseShown ? (
                    <StaticResponse text={conversation.response} />
                  ) : (
                    <TypingResponse
                      key={`rt-${conversationIndex}`}
                      text={conversation.response}
                      speed={computeResponseSpeed(conversation.response)}
                      onComplete={handleResponseComplete}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
